import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState } from "react";

import { ROLES } from "./config/authorizationConfig";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import useErrorHandler from "./hooks/useErrorHandler";

import AdminLogin from "./pages/AdminLogin";
import Navbar from "./components/NavBar/Navbar";
import Watermark from "./components/Watermark/Watermark";
import Error500 from "./pages/Error500";
// import AccessDenied from "./pages/AccessDenied";
import ErrorBoundary from "./ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AdminApp.scss";

import MarketplaceSidebar from "./components/marketPlaceSidebar/MarketplaceSidebar";
import AdminSidebar from "./components/AdminSidebar/AdminSidebar";
import AdminDashboard from "./pages/Admin/AdminDashboard";

// CRM module
import Crm from "./pages/Admin/Crm";

// Company module
import CompanyDetails from "./pages/Admin/CompanyDetails";
import Admin from "./pages/Admin/Admin";

import NotificationAdmin from "./pages/Notification/NotificationAdmin";
import AutomationAdmin from "./pages/Automation/AutomationAdmin";

// Stock QR scanning
import StockScanPage from "./pages/Admin/Inventory/StockScanPage";
import { capturePendingScan } from "./pages/Admin/Inventory/PendingScan";

// Runs once when the app loads, BEFORE any login redirect, so a scanned
// /stock-scan?... link is remembered and reopened after the user logs in.
capturePendingScan();

/* ---------------- LAYOUT WRAPPER ---------------- */
const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const shouldShowBackButton = location.pathname !== "/admin";

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.toggle("mobile-sidebar-open", isSidebarOpen);

    return () => document.body.classList.remove("mobile-sidebar-open");
  }, [isSidebarOpen]);

  return (
    <div className="app">
      <Navbar
        isSidebarOpen={isSidebarOpen}
        onToggleSidebar={() => setIsSidebarOpen((open) => !open)}
      />
      <AdminSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
      />
      {isSidebarOpen && (
        <button
          type="button"
          className="mobile-sidebar-backdrop"
          aria-label="Close navigation menu"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <MarketplaceSidebar />
      <Watermark text="HRLabs.in - Admin" />

      {shouldShowBackButton && (
        <button
          className="back-to-products-global-btn"
          onClick={() => navigate("/admin")}
        >
          ← Back to Products
        </button>
      )}

      <main className="main-content-admin">{children}</main>
    </div>
  );
};

/* ---------------- STOCK QR SCAN ROUTE ---------------- */
// Logged out -> go to login (the scanned link was saved by capturePendingScan and AdminLogin brings the
// user back to it). Logged in -> only INVENTORY-MANAGER may open the popup.
const StockScanRoute = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  return (
    <ProtectedRoute
      customCheck={(auth) =>
        !!auth.user?.authorities?.includes("ROLE_INVENTORY-MANAGER")
      }
    >
      <AdminLayout>
        <StockScanPage />
      </AdminLayout>
    </ProtectedRoute>
  );
};

/* ---------------- MAIN APP ---------------- */
function AdminApp() {
  useErrorHandler();

  return (
    <>
      <Router>
        <AuthProvider>
          <ErrorBoundary fallback={<Error500 />}>
            <ScrollToTop />

            <Routes>
              {/* PUBLIC LOGIN */}
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* ADMIN DASHBOARD */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute
                    roles={[
                      ROLES.ADMIN,
                      ROLES.HR,
                      ROLES.CRM_ADMIN,
                      ROLES.SALES_MANAGER,
                      ROLES.SALES_EMPLOYEE,
                    ]}
                  >
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* HRM + PAYROLL + ALL OTHER ADMIN PAGES */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute
                    roles={[
                      ROLES.ADMIN,
                      ROLES.HR,
                      ROLES.CRM_ADMIN,
                      ROLES.SALES_MANAGER,
                      ROLES.SALES_EMPLOYEE,
                    ]}
                  >
                    <AdminLayout>
                      <Admin />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* COMPANY DETAILS */}
              <Route
                path="/company-details/*"
                element={
                  <ProtectedRoute
                    roles={[ROLES.ADMIN, ROLES.CRM_ADMIN, ROLES.ERP_ADMIN]}
                  >
                    <AdminLayout>
                      <CompanyDetails />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* CRM */}
              <Route
                path="/crm/*"
                element={
                  <ProtectedRoute
                    roles={[
                      ROLES.CRM_ADMIN,
                      ROLES.ERP_ADMIN,
                      ROLES.INVENTORY_MANAGER,
                    ]}
                  >
                    <AdminLayout>
                      <Crm />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* STOCK QR SCAN (opened from a scanned QR code) */}
              <Route path="/stock-scan" element={<StockScanRoute />} />

              {/* NOTIFICATION */}
              <Route
                path="/admin/notification/*"
                element={
                  <ProtectedRoute roles={[ROLES.ADMIN]}>
                    <AdminLayout>
                      <NotificationAdmin />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* AUTOMATION */}
              <Route
                path="/admin/automation/*"
                element={
                  <ProtectedRoute roles={[ROLES.ADMIN]}>
                    <AdminLayout>
                      <AutomationAdmin />
                    </AdminLayout>
                  </ProtectedRoute>
                }
              />

              {/* ROOT → LOGIN */}
              <Route
                path="/"
                element={<Navigate to="/admin/login" replace />}
              />

              {/* 404 → LOGIN */}
              <Route
                path="*"
                element={<Navigate to="/admin/login" replace />}
              />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </Router>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999999 }}
      />
    </>
  );
}

export default AdminApp;
